
import { useEffect, useState } from "react";
import { ArrowRight, Calendar, FileSpreadsheet, User2, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import OngoingMFOCard, { MFO } from "@/components/dashboard/OngoingMFOCard";
import { Link, useNavigate } from "react-router-dom";
import { fetchEmployees } from "@/utils/supabase/employeeUtils";
import { fetchMFOs } from "@/utils/supabase/mfoUtils";
import { fetchDepartments } from "@/utils/supabase/departmentUtils";
import { Skeleton } from "@/components/ui/skeleton";
import { Employee } from "@/types/employee";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [mfos, setMFOs] = useState<MFO[]>([]);
  const [stats, setStats] = useState({
    totalMFOs: 0,
    ongoingTasks: 0,
    completedTasks: 0,
    totalStakeholders: 0
  });
  const [stakeholders, setStakeholders] = useState<Employee[]>([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Load MFOs, employees, and departments in parallel
        const [mfoData, employeeData, departmentData] = await Promise.all([
          fetchMFOs(),
          fetchEmployees(),
          fetchDepartments()
        ]);
        
        // Transform MFO data to the format needed by OngoingMFOCard
        const transformedMFOs: MFO[] = mfoData.map(mfo => ({
          id: mfo.id,
          title: mfo.title,
          description: mfo.successIndicators[0] || "No description available",
          progress: mfo.progress || 0, // Ensure progress property exists
          dueDate: mfo.dueDate,
          priority: mfo.priority as "high" | "medium" | "low",
          status: mfo.status as "not-started" | "in-progress" | "delayed" | "completed",
          stakeholders: mfo.collaborators.map(c => ({
            id: c.id,
            name: c.name,
            avatar: c.avatar
          }))
        }));

        const ongoing = mfoData.filter(m => m.status === "in-progress").length;
        const completed = mfoData.filter(m => m.status === "completed").length;
        
        setMFOs(transformedMFOs);
        setStakeholders(employeeData.slice(0, 4)); 
        
        setStats({
          totalMFOs: mfoData.length,
          ongoingTasks: ongoing,
          completedTasks: completed,
          totalStakeholders: employeeData.length
        });
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);


  const getStatusBadgeClass = (status: string) => {
    return status === "Active" 
      ? "bg-green-100 text-green-800" 
      : "bg-gray-100 text-gray-800";
  };

  const renderSkeletonCards = () => (
    <>
      {[1, 2, 3].map(i => (
        <Card key={i}>
          <CardContent className="p-5">
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full mb-4" />
            <div className="space-y-3">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-4 w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">Overview</h2>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/dashboard/calendar")}
          >
            <Calendar className="mr-2 h-4 w-4" />
            View Calendar
          </Button>
          <Button variant="outline" size="sm">
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total MFOs
            </CardTitle>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              className="h-4 w-4 text-muted-foreground"
            >
              <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
            </svg>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.totalMFOs}</div>
                <p className="text-xs text-muted-foreground">
                  Across all departments
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Ongoing Tasks
            </CardTitle>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              className="h-4 w-4 text-muted-foreground"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
              <path d="M8 12h8" />
              <path d="M12 8v8" />
            </svg>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.ongoingTasks}</div>
                <p className="text-xs text-muted-foreground">
                  In progress
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              className="h-4 w-4 text-muted-foreground"
            >
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.completedTasks}</div>
                <p className="text-xs text-muted-foreground">
                  Finalized MFOs
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Employees
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.totalStakeholders}</div>
                <p className="text-xs text-muted-foreground">
                  Active employees
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="space-y-1">
            <h3 className="text-xl font-semibold tracking-tight flex items-center">
              <FileSpreadsheet className="mr-2 h-5 w-5" /> 
              List of On-going MFOs
            </h3>
            <p className="text-sm text-muted-foreground">
              Monitor current projects and their progress
            </p>
          </div>
          <Link to="/dashboard/mfo">
            <Button variant="outline" size="sm">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? (
            renderSkeletonCards()
          ) : mfos.length > 0 ? (
            mfos.slice(0, 6).map((mfo) => (
              <OngoingMFOCard key={mfo.id} mfo={mfo} />
            ))
          ) : (
            <div className="col-span-full p-8 text-center border rounded-lg">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-medium mb-2">No ongoing MFOs</h3>
              <p className="text-sm text-muted-foreground mb-4">
                There are no MFOs currently in progress.
              </p>
              <Button onClick={() => navigate('/dashboard/mfo')}>View All MFOs</Button>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="space-y-1">
            <h3 className="text-xl font-semibold tracking-tight flex items-center">
              <Users className="mr-2 h-5 w-5" /> 
              Employees
            </h3>
            <p className="text-sm text-muted-foreground">
              Collaborators and project participants
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/dashboard/employees')}
          >
            View All <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4">
                <Skeleton className="h-12 w-full mb-4" />
                <Skeleton className="h-12 w-full mb-4" />
                <Skeleton className="h-12 w-full mb-4" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : stakeholders.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b transition-colors hover:bg-muted/50">
                    <th className="text-left p-4 text-sm font-medium">Name</th>
                    <th className="text-left p-4 text-sm font-medium">Role</th>
                    <th className="text-left p-4 text-sm font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stakeholders.map((employee) => (
                    <tr
                      key={employee.id}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      <td className="p-4 align-middle text-sm">
                        <div className="flex items-center gap-3">
                          <User2 className="h-5 w-5 text-muted-foreground" />
                          <span>{employee.name}</span>
                        </div>
                      </td>
                      <td className="p-4 align-middle text-sm">{employee.position}</td>
                      <td className="p-4 align-middle text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          getStatusBadgeClass(employee.status)
                        }`}>
                          {employee.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">No stakeholders</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  There are no employees in the system yet.
                </p>
                <Button onClick={() => navigate('/dashboard/employees')}>View All Employees</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
